//@flow
import React, {useReducer, useState, useEffect} from 'react';
import {reducer, initialLocations} from './locationReducer';
import LocationItem from './LocationItem';
import {fetchAdmin1} from '../../../../../../../utils/location';
import {LOCATION_ACTIONS} from './type';
import {Typography, Icon, makeStyles, Collapse} from '@material-ui/core';
import CustomButton from '../../../../../../../elements/CustomButton/CustomButton';
import {Delete, ExpandLess, ExpandMore} from '@material-ui/icons';
import {colors} from '../../../../../../../assets/styles/colors';
import {getConfigurationAdmin} from '../../../../../../../utils/location';
import {useToggle} from '../../../../../../../utils/react';

import type {Country} from '../../../../../../../entities';
import type {Admin1Type} from '../../../../../../../utils/location/api';
import type {DefaultSelectedAdmins} from './type';
import {
  isIndeterminate,
  getCheckedState,
  isDisabledAdmin1,
  hasTrueValues,
} from './utils';
import {
  useClearAllLocationsSelected,
  useDataShippingAdvanced,
} from '../../../utils';

const useStyles = makeStyles(theme => ({
  root: {
    border: '1px solid #F4F4F6',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    borderBottom: '1px solid #F4F4F6',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  buttonDelete: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  buttonExpand: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: colors.grey700,
  },
  deleteIcon: {
    color: '#F44336',
  },
  deleteIconDisabled: {
    color: colors.grey500,
  },
  deleteText: {
    color: '#F44336',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  deleteTextDisabled: {
    color: colors.grey500,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  contentRows: {
    // padding: '10px',
  },
  containerAdmins: {
    maxHeight: '500px',
    overflowY: 'auto',
  },
}));

type LocationSelectorProps = {
  country: Country,
  defaultSelectedAdmins?: DefaultSelectedAdmins,
  setLocationsSelected: Function,
  locationsSelected: String[],
};

const defaultSelectedAdminsData = {
  // '1': {
  //   '21': true,
  //   '22': true,
  // },
  // '2': true,
  // '3': true,
};

export default function LocationSelector({
  country,
  defaultSelectedAdmins = defaultSelectedAdminsData,
  setLocationsSelected,
  locationsSelected,
}: LocationSelectorProps) {
  const classes = useStyles();

  const [locations, dispatch] = useReducer(reducer, initialLocations);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {shippingDataAdvanced} = useDataShippingAdvanced();
  const {clearAllLocationsSelected} = useClearAllLocationsSelected();

  const defaultFormatedSelectedAdmins: DefaultSelectedAdmins = country?.code
    ? // $FlowFixMe
      defaultSelectedAdmins[country?.code]
    : {};

  const [collapseOpen, toggleCollapse] = useToggle(false);

  const adminCountryConfig = getConfigurationAdmin(country?.code);

  const isEnabledButtonClear =
    locationsSelected.length > 0 ||
    // $FlowFixMe
    hasTrueValues(defaultSelectedAdmins[country?.code] || {});

  const handleToggleExpand = async () => {
    toggleCollapse();
    if (locations.length > 0) return;
    setLoading(true);
    setError(null);
    try {
      const admin1Locations: Admin1Type[] = await fetchAdmin1(country?.code);

      dispatch({
        type: LOCATION_ACTIONS.SET_INITIAL_LOCATIONS,
        indexes: [],
        subLocations: admin1Locations.map(admin => ({
          adminId: String(admin.admin1Id),
          adminName: admin.admin1Name,
          isExpanded: false,

          isChecked: getCheckedState(
            String(admin.admin1Id),
            defaultFormatedSelectedAdmins
          ),
          indeterminate: isIndeterminate(
            String(admin.admin1Id),
            defaultFormatedSelectedAdmins
          ),
          isDisabled: isDisabledAdmin1(
            String(admin.admin1Id),
            country?.code,
            admin,
            shippingDataAdvanced,
            {
              isChecked: getCheckedState(
                String(admin.admin1Id),
                defaultFormatedSelectedAdmins
              ),
            }
          ),
          subLocations: [],
        })),
      });
    } catch (error) {
      console.error('Failed to fetch admin1 locations:', error);
      setError('Error al cargar las localidades');
    }
    setLoading(false);
  };

  const handleClearAll = (countryCode: string) => {
    dispatch({type: LOCATION_ACTIONS.CLEAR_ALL});
    clearAllLocationsSelected(countryCode);
  };

  useEffect(() => {
    const getCheckedLocations = locations => {
      let selectedLocations = [];
      for (let location of locations) {
        if (location.isChecked) {
          selectedLocations.push(location.adminName);
        }
        if (location.subLocations && location.subLocations.length > 0) {
          const subLocations = getCheckedLocations(location.subLocations);
          selectedLocations = [...selectedLocations, ...subLocations];
        }
      }
      return selectedLocations;
    };

    const selected = getCheckedLocations(locations);
    if (JSON.stringify(selected) !== JSON.stringify(locationsSelected)) {
      setLocationsSelected(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, setLocationsSelected]);

  useEffect(() => {
    if (defaultFormatedSelectedAdmins && country && shippingDataAdvanced) {
      handleToggleExpand();
      handleToggleExpand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFormatedSelectedAdmins, toggleCollapse, shippingDataAdvanced]);

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.title}>{country?.name}</Typography>
          <div className={classes.headerActions}>
            <CustomButton
              variant="text"
              color="default"
              className={classes.buttonDelete}
              onClick={() => handleClearAll(country.code)}
              disabled={loading || !isEnabledButtonClear}
            >
              <Icon
                className={
                  !isEnabledButtonClear
                    ? classes.deleteIconDisabled
                    : classes.deleteIcon
                }
              >
                <Delete />
              </Icon>
              <Typography
                className={
                  !isEnabledButtonClear
                    ? classes.deleteTextDisabled
                    : classes.deleteText
                }
              >
                Borrar Todo
              </Typography>
            </CustomButton>

            <CustomButton
              variant="text"
              color="default"
              onClick={handleToggleExpand}
              className={classes.buttonExpand}
            >
              {collapseOpen ? <ExpandLess /> : <ExpandMore />}
            </CustomButton>
          </div>
        </div>
        <div className={classes.containerAdmins}>
          <Collapse in={collapseOpen}>
            {loading && <div>Loading...</div>}
            {error && <div style={{color: 'red'}}>{error}</div>}
            {!loading &&
              !error &&
              locations.map((location, index) => (
                <LocationItem
                  adminCountryConfig={adminCountryConfig}
                  key={location.adminName}
                  location={location}
                  indexes={[index]}
                  country={country?.code}
                  dispatch={dispatch}
                  locations={locations}
                  defaultSelectedAdmins={defaultFormatedSelectedAdmins}
                  canExpand
                  shippingDataAdvanced={shippingDataAdvanced}
                />
              ))}
          </Collapse>
        </div>
      </div>
    </>
  );
}
